import { LoaderIcon, RadioButtonGroup } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Stack } from 'src/packages/data/structures/Stack';

import type { BaseSettingsScreen } from '../screen/BaseSettingsScreen';

import type { AlertsEntity } from './Alerts.entity';
import { AlertsGeneralSettingsStore } from './AlertsGeneralSettingsStore';
import { AlertsGeneralTab } from './AlertsGeneralTab';
import { AlertsPersonalSettingsStore } from './AlertsPersonalSettingsStore';
import { AlertsPersonalTab } from './AlertsPersonalTab';
import styles from './AlertsScreen.module.scss';
import { RecommendationsTable } from './RecommendationsTable';

type TAlertsTab = 'general' | 'personal' | 'recommendations';

type Props = {
  stack: Stack<BaseSettingsScreen>;
  screen: AlertsEntity;
};

export const AlertsScreen = observer(function AlertsScreen(_props: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TAlertsTab>('general');
  const [generalStore] = useState(() => new AlertsGeneralSettingsStore());
  const [personalStore] = useState(() => new AlertsPersonalSettingsStore());

  const isLoading =
    activeTab === 'general' ? generalStore.isLoading : activeTab === 'personal' ? personalStore.isLoading : false;

  const tabsSwitcher = (
    <RadioButtonGroup<TAlertsTab>
      size="s"
      value={activeTab}
      items={[
        { value: 'general', label: t('settings:Alerts:tabGeneral') },
        { value: 'personal', label: t('settings:Alerts:tabPersonal') },
        { value: 'recommendations', label: t('settings:Alerts:tabRecommendations') },
      ]}
      onChange={setActiveTab}
      style={{ whiteSpace: 'nowrap' }}
    />
  );

  return (
    <article className={styles.column}>
      {activeTab !== 'recommendations' && <div className={styles.tabs}>{tabsSwitcher}</div>}

      <div className={styles.contentWrapper}>
        {activeTab === 'general' && <AlertsGeneralTab store={generalStore} />}
        {activeTab === 'personal' && <AlertsPersonalTab store={personalStore} />}
        {activeTab === 'recommendations' && <RecommendationsTable tabsSwitcher={tabsSwitcher} />}
      </div>

      {isLoading && (
        <div className={styles.loading}>
          <LoaderIcon />
        </div>
      )}
    </article>
  );
});
